import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from '@apollo/client/react';
import { Badge, Box, BoxProps, Button, chakra, HStack, IconButton, Spinner, Text } from '@chakra-ui/react';
import { HoverCardContent, HoverCardRoot, HoverCardTrigger } from '@src/components/chakra-ui/hover-card';
import { Tooltip } from '@src/components/chakra-ui/tooltip';
import { ChatGenerateControl } from '@src/components/sco/Chat/ChatGenerateControl';
import { ChatTypingIndicator } from '@src/components/sco/Chat/ChatThreadTypingIndicator';
import { ChatConnectionStatus } from '@src/components/sco/Chat/Layout/ChatConnectionStatus';
import { draftKey, liveFiles, sameDraftKey, serverDraftAction, serverDraftKey } from '@src/components/sco/Chat/chatCrmDraft';
import { useTypingPresence } from '@src/components/sco/Chat/composer/useTypingPresence';
import { useCrmChatDraft } from '@src/components/sco/Chat/useCrmChatDraft';
import { type CommentAttachmentFile, CommentPendingAttachments, COMMENT_STORED_FILES_SELECTION, mapInitialFiles, useCommentAttachments } from '@src/components/sco/Comments/CommentAttachments';
import { OnboardingHighlightTarget, ONBOARDING_HIGHLIGHT_IDS } from '@src/components/sco/Requests/RequestOnboarding/OnboardingHighlightTarget';
import { editorTypographyStyles, RichMarkdownInput } from '@src/components/sco/RichMarkdownInput';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { gql } from '@src/lib/gql';
import React, { useEffect, useRef, useState } from 'react';
import { LuArrowUp, LuPlus, LuSave, LuSend, LuTrash2 } from 'react-icons/lu';

// The composer looks like the chat bubble its text becomes
const CHAT_EDITOR_TYPOGRAPHY = editorTypographyStyles('chat-message');
type ChatDraft = {
  commentId?: string | null;
  body?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  storedFiles?: {
    items?: Array<CommentAttachmentFile | null> | null;
  } | null;
};
type Props = {
  boxProps?: BoxProps;
  newCommentValues: {
    attachmentId?: string;
    attachmentType?: string;
    parentCommentId?: string;
    parentCommentNodeId?: string;
    threadId?: string;
  };
  onSuccess?: (mutationResponse) => Promise<any> | any;
  onError?: (error) => Promise<any> | any;
  onCancel?: () => Promise<any> | any;
  placeholder?: string;
  enableFileAttachments?: boolean;
};

/**
 * Runs `task`, then `onFinally`; an error goes to `onError` when given, and is rethrown otherwise.
 * The composer's handlers use this instead of their own try/catch/finally, which the React Compiler
 * (1.0.0) cannot compile; with it the whole composer stays uncompiled.
 */
async function guarded(task: () => Promise<void>, handlers: {
  onError?: (err: unknown) => Promise<void> | void;
  onFinally?: () => void;
}) {
  try {
    await task();
  } catch (err) {
    if (!handlers.onError) {
      throw err;
    }
    await handlers.onError(err);
  } finally {
    handlers.onFinally?.();
  }
}
export const ChatMessageComposer = props => {
  const $ = _c(223);
  const {
    currentUser
  } = useCurrentUser();
  const [hasText, setHasText] = useState(false);
  const [draftCommentId, setDraftCommentId] = useState(null);
  const [editorSeed, setEditorSeed] = useState("new");
  const [initialMarkdown, setInitialMarkdown] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [submitTrigger, setSubmitTrigger] = useState(0);
  const [submissionCount, setSubmissionCount] = useState(0);
  const [generateBusy, setGenerateBusy] = useState(false);
  const {
    pendingFiles,
    addFile,
    removeFile,
    reset,
    isUploading,
    isFull,
    storedFileIds,
    setPendingFiles
  } = useCommentAttachments();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = ChatMessageComposer.mutations.upsertChatComment();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [upsertChatComment, upsertChatCommentMutation] = useMutation(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ChatMessageComposer.mutations.deleteComment();
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [deleteComment] = useMutation(t1);
  const inFlightRef = useRef(false);
  const threadId = props.newCommentValues.threadId;
  const parentCommentId = props.newCommentValues.parentCommentId;
  const attachmentType = props.newCommentValues.parentCommentId ? "Comment" : "Thread";
  const attachmentId = props.newCommentValues.parentCommentId || props.newCommentValues.threadId;
  const t2 = currentUser?.userId;
  let t3;
  if ($[2] !== attachmentId || $[3] !== attachmentType || $[4] !== t2) {
    t3 = {
      attachmentType,
      attachmentId,
      userId: t2
    };
    $[2] = attachmentId;
    $[3] = attachmentType;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const presence = useTypingPresence(t3);
  const threadIdRef = useRef(threadId);
  let t4;
  let t5;
  if ($[6] !== threadId) {
    t4 = () => {
      threadIdRef.current = threadId;
    };
    t5 = [threadId];
    $[6] = threadId;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  const t6 = !!currentUser;
  const t7 = currentUser?.userId;
  let t8;
  if ($[9] !== parentCommentId || $[10] !== t6 || $[11] !== t7 || $[12] !== threadId) {
    t8 = {
      enabled: t6,
      parentCommentId,
      threadId,
      userId: t7
    };
    $[9] = parentCommentId;
    $[10] = t6;
    $[11] = t7;
    $[12] = threadId;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const crm = useCrmChatDraft(t8);
  const crmRef = useRef(crm);
  let t9;
  if ($[14] !== crm) {
    t9 = () => {
      crmRef.current = crm;
    };
    $[14] = crm;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  useEffect(t9);
  const crmOn = crm.on;
  const canUseDrafts = !!currentUser?.isAdmin || crmOn;
  const crmDraft = crmOn ? crm.context?.draft ?? null : null;
  const [crmOpen, setCrmOpen] = useState(false);
  const crmLockedBy = crmOpen ? crm.lockedBy : null;
  const bodyRef = useRef("");
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = [];
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const fileIdsRef = useRef(t10);
  let t11;
  let t12;
  if ($[17] !== storedFileIds) {
    t11 = () => {
      fileIdsRef.current = storedFileIds;
    };
    t12 = [storedFileIds];
    $[17] = storedFileIds;
    $[18] = t11;
    $[19] = t12;
  } else {
    t11 = $[18];
    t12 = $[19];
  }
  useEffect(t11, t12);
  const crmSavedKey = useRef(null);
  const [crmConflict, setCrmConflict] = useState(false);
  const [crmNote, setCrmNote] = useState(null);
  const [crmError, setCrmError] = useState(null);
  let t13;
  if ($[20] !== crm.context || $[21] !== crmOn) {
    t13 = crmOn ? serverDraftKey(crm.context) : null;
    $[20] = crm.context;
    $[21] = crmOn;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const crmServerKey = t13;
  let t14;
  if ($[23] !== presence) {
    t14 = (text, t15) => {
      const typed = t15 === undefined ? false : t15;
      bodyRef.current = text;
      const nonEmpty = stripMarkdownWhitespace(text).length > 0;
      setHasText(nonEmpty);
      if (typed || !nonEmpty) {
        presence.noteText(nonEmpty);
      }
    };
    $[23] = presence;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  const setBoxBody = t14;
  let t15;
  if ($[25] !== setBoxBody || $[26] !== setPendingFiles) {
    t15 = t16 => {
      const force = t16 === undefined ? false : t16;
      const key = serverDraftKey(crmRef.current.context);
      crmSavedKey.current = key;
      setCrmConflict(false);
      if (!force && sameDraftKey(draftKey(bodyRef.current, fileIdsRef.current), key)) {
        return;
      }
      const draft = crmRef.current.context?.draft;
      const text_0 = draft?.body || "";
      setBoxBody(text_0);
      setInitialMarkdown(text_0);
      setEditorSeed(`crm-${Date.now()}`);
      setPendingFiles(mapInitialFiles(liveFiles(draft?.storedFiles)));
    };
    $[25] = setBoxBody;
    $[26] = setPendingFiles;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  const placeCrmDraft = t15;
  let t16;
  if ($[28] !== placeCrmDraft) {
    t16 = () => {
      setDraftCommentId(null);
      setCrmError(null);
      setCrmNote(null);
      setCrmOpen(true);
      placeCrmDraft(true);
    };
    $[28] = placeCrmDraft;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  const openCrmDraft = t16;
  let t17;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => {
      setCrmOpen(false);
      crmSavedKey.current = null;
      setCrmConflict(false);
    };
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  const closeCrmDraft = t17;
  const canOfferGenerate = !!currentUser?.isScoAdmin && !parentCommentId && !!threadId;
  let t18;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = ChatMessageComposer.queries.myDrafts();
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  const t19 = !threadId || !canUseDrafts;
  const t20 = `ChatThread:${threadId}:${parentCommentId || 0}:drafts`;
  let t21;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = [["updatedAt", "DESC"]];
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  const t22 = parentCommentId || null;
  let t23;
  if ($[33] !== t20 || $[34] !== t22 || $[35] !== threadId) {
    t23 = {
      collectionId: t20,
      order: t21,
      pageSize: 50,
      parentCommentId: t22,
      threadId
    };
    $[33] = t20;
    $[34] = t22;
    $[35] = threadId;
    $[36] = t23;
  } else {
    t23 = $[36];
  }
  let t24;
  if ($[37] !== t19 || $[38] !== t23) {
    t24 = {
      skip: t19,
      variables: t23
    };
    $[37] = t19;
    $[38] = t23;
    $[39] = t24;
  } else {
    t24 = $[39];
  }
  const draftsQuery = useQuery(t18, t24);
  let t25;
  if ($[40] !== draftsQuery.data?.forum?.comments?.items) {
    t25 = draftsQuery.data?.forum?.comments?.items || [];
    $[40] = draftsQuery.data?.forum?.comments?.items;
    $[41] = t25;
  } else {
    t25 = $[41];
  }
  const drafts = t25;
  let t26;
  if ($[42] !== draftCommentId || $[43] !== props.enableFileAttachments || $[44] !== props.newCommentValues || $[45] !== storedFileIds) {
    t26 = isDraft => {
      const allowFiles = props.enableFileAttachments !== false;
      const fileIds = allowFiles ? storedFileIds : [];
      return {
        body: bodyRef.current || "",
        ...props.newCommentValues,
        ...(draftCommentId ? {
          commentId: draftCommentId
        } : {}),
        isDraft,
        ...(fileIds.length > 0 || draftCommentId ? {
          storedFileIds: fileIds
        } : {})
      };
    };
    $[42] = draftCommentId;
    $[43] = props.enableFileAttachments;
    $[44] = props.newCommentValues;
    $[45] = storedFileIds;
    $[46] = t26;
  } else {
    t26 = $[46];
  }
  const commentInput = t26;
  let t27;
  if ($[47] !== props.enableFileAttachments || $[48] !== storedFileIds) {
    t27 = () => {
      const allowFiles_0 = props.enableFileAttachments !== false;
      const fileIds_0 = allowFiles_0 ? storedFileIds : [];
      return stripMarkdownWhitespace(bodyRef.current).length > 0 || fileIds_0.length > 0;
    };
    $[47] = props.enableFileAttachments;
    $[48] = storedFileIds;
    $[49] = t27;
  } else {
    t27 = $[49];
  }
  const canCompose = t27;
  let t28;
  if ($[50] !== crmLockedBy || $[51] !== crmOpen || $[52] !== isUploading || $[53] !== props || $[54] !== reset || $[55] !== setBoxBody) {
    t28 = async () => {
      if (crmLockedBy || isUploading || inFlightRef.current) {
        return;
      }
      if (stripMarkdownWhitespace(bodyRef.current).length === 0) {
        setCrmError("Write a message to send with your files.");
        return;
      }
      inFlightRef.current = true;
      setIsLoading(true);
      await guarded(async () => {
        const commentId = crmOpen ? crmRef.current.context?.draft?.commentId || null : null;
        const result = await crmRef.current.send(bodyRef.current, fileIdsRef.current, commentId);
        if (!result.ok) {
          setCrmError(result.error);
          return;
        }
        setCrmError(null);
        setCrmNote(null);
        closeCrmDraft();
        setBoxBody("");
        setInitialMarkdown("");
        setEditorSeed(`sent-${Date.now()}`);
        reset();
        setSubmissionCount(_temp);
        crmRef.current.refetch();
        if (props.onSuccess) {
          await props.onSuccess(result);
        }
      }, {
        onFinally: () => {
          inFlightRef.current = false;
          setIsLoading(false);
        }
      });
    };
    $[50] = crmLockedBy;
    $[51] = crmOpen;
    $[52] = isUploading;
    $[53] = props;
    $[54] = reset;
    $[55] = setBoxBody;
    $[56] = t28;
  } else {
    t28 = $[56];
  }
  const sendCrmReply = t28;
  let t29;
  if ($[57] !== crmLockedBy || $[58] !== isUploading) {
    t29 = async () => {
      if (crmLockedBy || isUploading || inFlightRef.current) {
        return;
      }
      if (stripMarkdownWhitespace(bodyRef.current).length === 0) {
        setCrmError("Write the reply before saving it.");
        return;
      }
      inFlightRef.current = true;
      setIsLoading(true);
      await guarded(async () => {
        crmRef.current.claim();
        const result_0 = await crmRef.current.save(bodyRef.current, fileIdsRef.current);
        if (!result_0.ok) {
          setCrmError(result_0.error);
          return;
        }
        crmSavedKey.current = draftKey(bodyRef.current, fileIdsRef.current);
        setDraftCommentId(null);
        setCrmOpen(true);
        setCrmConflict(false);
        setCrmError(null);
        setCrmNote("Saved. Your CRM inbox has the same draft.");
        crmRef.current.refetch();
      }, {
        onFinally: () => {
          inFlightRef.current = false;
          setIsLoading(false);
        }
      });
    };
    $[57] = crmLockedBy;
    $[58] = isUploading;
    $[59] = t29;
  } else {
    t29 = $[59];
  }
  const saveCrmDraft = t29;
  let clearComposer;
  let deleteCrmDraft;
  let handleSubmit;
  let sendCrmDraft;
  if ($[60] !== canCompose || $[61] !== commentInput || $[62] !== crm.lockedBy || $[63] !== crmOn || $[64] !== crmOpen || $[65] !== draftCommentId || $[66] !== draftsQuery || $[67] !== isUploading || $[68] !== props || $[69] !== reset || $[70] !== sendCrmReply || $[71] !== setBoxBody || $[72] !== upsertChatComment) {
    sendCrmDraft = async () => {
      const draft_0 = crmRef.current.context?.draft;
      if (!draft_0?.commentId || crm.lockedBy || inFlightRef.current) {
        return;
      }
      inFlightRef.current = true;
      setIsLoading(true);
      await guarded(async () => {
        const result_1 = await crmRef.current.send(draft_0.body || "", null, draft_0.commentId);
        if (!result_1.ok) {
          setCrmError(result_1.error);
          return;
        }
        setCrmError(null);
        if (crmOpen) {
          clearComposer(`sent-${Date.now()}`);
          setSubmissionCount(_temp2);
        }
        crmRef.current.refetch();
        if (props.onSuccess) {
          await props.onSuccess(result_1);
        }
      }, {
        onFinally: () => {
          inFlightRef.current = false;
          setIsLoading(false);
        }
      });
    };
    deleteCrmDraft = async () => {
      if (crm.lockedBy || inFlightRef.current) {
        return;
      }
      inFlightRef.current = true;
      setIsLoading(true);
      await guarded(async () => {
        const result_2 = await crmRef.current.discard();
        if (!result_2.ok) {
          setCrmError(result_2.error);
          return;
        }
        setCrmError(null);
        if (crmOpen) {
          clearComposer(`cleared-${Date.now()}`);
        }
        crmRef.current.refetch();
      }, {
        onFinally: () => {
          inFlightRef.current = false;
          setIsLoading(false);
        }
      });
    };
    let t30;
    if ($[77] !== canCompose || $[78] !== commentInput || $[79] !== crmOn || $[80] !== draftCommentId || $[81] !== draftsQuery || $[82] !== isUploading || $[83] !== props || $[84] !== reset || $[85] !== sendCrmReply || $[86] !== setBoxBody || $[87] !== upsertChatComment) {
      t30 = async () => {
        if (crmOn && !draftCommentId) {
          await sendCrmReply();
          return;
        }
        if (!canCompose() || isUploading || inFlightRef.current) {
          return;
        }
        inFlightRef.current = true;
        let mutationResponse = null;
        setIsLoading(true);
        await guarded(async () => {
          mutationResponse = await upsertChatComment({
            variables: {
              input: commentInput(false)
            }
          });
          setBoxBody("");
          setDraftCommentId(null);
          setInitialMarkdown("");
          setEditorSeed(`sent-${Date.now()}`);
          reset();
          await draftsQuery.refetch();
          setSubmissionCount(_temp3);
          if (props.onSuccess) {
            await props.onSuccess(mutationResponse);
          }
        }, {
          onError: async err => {
            if (props.onError) {
              await props.onError(err);
            } else {
              alert("Comment could not be posted.");
            }
          },
          onFinally: () => {
            inFlightRef.current = false;
          }
        });
        setIsLoading(false);
      };
      $[77] = canCompose;
      $[78] = commentInput;
      $[79] = crmOn;
      $[80] = draftCommentId;
      $[81] = draftsQuery;
      $[82] = isUploading;
      $[83] = props;
      $[84] = reset;
      $[85] = sendCrmReply;
      $[86] = setBoxBody;
      $[87] = upsertChatComment;
      $[88] = t30;
    } else {
      t30 = $[88];
    }
    handleSubmit = t30;
    clearComposer = seed => {
      setDraftCommentId(null);
      setCrmOpen(false);
      crmSavedKey.current = null;
      setCrmConflict(false);
      setBoxBody("");
      setInitialMarkdown("");
      setEditorSeed(seed);
      reset();
    };
    $[60] = canCompose;
    $[61] = commentInput;
    $[62] = crm.lockedBy;
    $[63] = crmOn;
    $[64] = crmOpen;
    $[65] = draftCommentId;
    $[66] = draftsQuery;
    $[67] = isUploading;
    $[68] = props;
    $[69] = reset;
    $[70] = sendCrmReply;
    $[71] = setBoxBody;
    $[72] = upsertChatComment;
    $[73] = clearComposer;
    $[74] = deleteCrmDraft;
    $[75] = handleSubmit;
    $[76] = sendCrmDraft;
  } else {
    clearComposer = $[73];
    deleteCrmDraft = $[74];
    handleSubmit = $[75];
    sendCrmDraft = $[76];
  }
  let t30;
  if ($[89] !== canCompose || $[90] !== clearComposer || $[91] !== commentInput || $[92] !== crmOn || $[93] !== crmOpen || $[94] !== draftCommentId || $[95] !== draftsQuery || $[96] !== isUploading || $[97] !== props || $[98] !== saveCrmDraft || $[99] !== upsertChatComment) {
    t30 = async () => {
      if (crmOn && (crmOpen || !draftCommentId && !crmRef.current.context?.draft)) {
        await saveCrmDraft();
        return;
      }
      if (!canCompose() || isUploading || inFlightRef.current) {
        return;
      }
      inFlightRef.current = true;
      const updatingOpenDraft = !!draftCommentId;
      setIsLoading(true);
      await guarded(async () => {
        await upsertChatComment({
          variables: {
            input: commentInput(true)
          }
        });
        await draftsQuery.refetch();
        if (!updatingOpenDraft) {
          clearComposer(`saved-${Date.now()}`);
        }
      }, {
        onError: async err_0 => {
          if (props.onError) {
            await props.onError(err_0);
          } else {
            alert("Draft could not be saved.");
          }
        },
        onFinally: () => {
          inFlightRef.current = false;
        }
      });
      setIsLoading(false);
    };
    $[89] = canCompose;
    $[90] = clearComposer;
    $[91] = commentInput;
    $[92] = crmOn;
    $[93] = crmOpen;
    $[94] = draftCommentId;
    $[95] = draftsQuery;
    $[96] = isUploading;
    $[97] = props;
    $[98] = saveCrmDraft;
    $[99] = upsertChatComment;
    $[100] = t30;
  } else {
    t30 = $[100];
  }
  const saveDraft = t30;
  let t31;
  if ($[101] !== clearComposer || $[102] !== draftCommentId || $[103] !== draftsQuery || $[104] !== isUploading || $[105] !== props || $[106] !== upsertChatComment) {
    t31 = async draft_1 => {
      if (!draft_1.commentId || isUploading || inFlightRef.current) {
        return;
      }
      inFlightRef.current = true;
      setIsLoading(true);
      await guarded(async () => {
        const mutationResponse_0 = await upsertChatComment({
          variables: {
            input: {
              body: draft_1.body || "",
              ...props.newCommentValues,
              commentId: draft_1.commentId,
              isDraft: false
            }
          }
        });
        if (draftCommentId === draft_1.commentId) {
          clearComposer(`sent-${Date.now()}`);
          setSubmissionCount(_temp4);
        }
        await draftsQuery.refetch();
        if (props.onSuccess) {
          await props.onSuccess(mutationResponse_0);
        }
      }, {
        onError: async err_1 => {
          if (props.onError) {
            await props.onError(err_1);
          } else {
            alert("Draft could not be posted.");
          }
        },
        onFinally: () => {
          inFlightRef.current = false;
        }
      });
      setIsLoading(false);
    };
    $[101] = clearComposer;
    $[102] = draftCommentId;
    $[103] = draftsQuery;
    $[104] = isUploading;
    $[105] = props;
    $[106] = upsertChatComment;
    $[107] = t31;
  } else {
    t31 = $[107];
  }
  const sendDraft = t31;
  let t32;
  if ($[108] !== setBoxBody || $[109] !== setPendingFiles) {
    t32 = draft_2 => {
      if (!draft_2.commentId) {
        return;
      }
      const nextBody = draft_2.body || "";
      closeCrmDraft();
      setCrmNote(null);
      setDraftCommentId(draft_2.commentId);
      setBoxBody(nextBody);
      setInitialMarkdown(nextBody);
      setEditorSeed(`${draft_2.commentId}-${Date.now()}`);
      setPendingFiles(mapInitialFiles(draft_2.storedFiles?.items));
    };
    $[108] = setBoxBody;
    $[109] = setPendingFiles;
    $[110] = t32;
  } else {
    t32 = $[110];
  }
  const loadDraft = t32;
  let t33;
  if ($[111] !== deleteComment || $[112] !== draftCommentId || $[113] !== draftsQuery || $[114] !== props || $[115] !== setBoxBody) {
    t33 = async commentId_0 => {
      setIsLoading(true);
      ;
      try {
        await deleteComment({
          variables: {
            input: {
              commentId: commentId_0
            }
          }
        });
        if (draftCommentId === commentId_0) {
          setDraftCommentId(null);
          setBoxBody("");
          setInitialMarkdown("");
          setEditorSeed(`cleared-${Date.now()}`);
        }
        await draftsQuery.refetch();
      } catch (t34) {
        const err_2 = t34;
        if (props.onError) {
          await props.onError(err_2);
        } else {
          alert("Draft could not be deleted.");
        }
      }
      setIsLoading(false);
    };
    $[111] = deleteComment;
    $[112] = draftCommentId;
    $[113] = draftsQuery;
    $[114] = props;
    $[115] = setBoxBody;
    $[116] = t33;
  } else {
    t33 = $[116];
  }
  const removeDraft = t33;
  let t34;
  if ($[117] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = () => {
      setCrmOpen(false);
      crmSavedKey.current = null;
      setCrmConflict(false);
      setCrmError(null);
      setCrmNote(null);
    };
    $[117] = t34;
  } else {
    t34 = $[117];
  }
  let t35;
  if ($[118] !== threadId) {
    t35 = [threadId];
    $[118] = threadId;
    $[119] = t35;
  } else {
    t35 = $[119];
  }
  useEffect(t34, t35);
  let t36;
  if ($[120] !== crmLockedBy || $[121] !== crmOn || $[122] !== crmOpen || $[123] !== crmServerKey || $[124] !== placeCrmDraft) {
    t36 = () => {
      if (!crmOn || !crmOpen || crmServerKey == null || crmSavedKey.current == null) {
        return;
      }
      if (!crmRef.current.context?.draft) {
        closeCrmDraft();
        setCrmNote("This draft was sent or cleared in the CRM. Your text is still here.");
        return;
      }
      if (crmLockedBy) {
        placeCrmDraft();
        return;
      }
      const action = serverDraftAction({
        boxKey: draftKey(bodyRef.current, fileIdsRef.current),
        savedKey: crmSavedKey.current,
        serverKey: crmServerKey
      });
      if (action === "adopt") {
        placeCrmDraft();
      } else {
        if (action === "conflict") {
          setCrmConflict(true);
        }
      }
    };
    $[120] = crmLockedBy;
    $[121] = crmOn;
    $[122] = crmOpen;
    $[123] = crmServerKey;
    $[124] = placeCrmDraft;
    $[125] = t36;
  } else {
    t36 = $[125];
  }
  let t37;
  if ($[126] !== crmLockedBy || $[127] !== crmOn || $[128] !== crmOpen || $[129] !== crmServerKey) {
    t37 = [crmOn, crmOpen, crmServerKey, crmLockedBy];
    $[126] = crmLockedBy;
    $[127] = crmOn;
    $[128] = crmOpen;
    $[129] = crmServerKey;
    $[130] = t37;
  } else {
    t37 = $[130];
  }
  useEffect(t36, t37);
  let t38;
  if ($[131] !== handleSubmit || $[132] !== submitTrigger) {
    t38 = () => {
      if (submitTrigger > 0) {
        handleSubmit();
      }
    };
    $[131] = handleSubmit;
    $[132] = submitTrigger;
    $[133] = t38;
  } else {
    t38 = $[133];
  }
  let t39;
  if ($[134] !== submitTrigger) {
    t39 = [submitTrigger];
    $[134] = submitTrigger;
    $[135] = t39;
  } else {
    t39 = $[135];
  }
  useEffect(t38, t39);
  if (!currentUser) {
    let t40;
    if ($[136] === Symbol.for("react.memo_cache_sentinel")) {
      t40 = <Spinner />;
      $[136] = t40;
    } else {
      t40 = $[136];
    }
    return t40;
  }
  let t40;
  if ($[137] === Symbol.for("react.memo_cache_sentinel")) {
    t40 = {
      containerType: "inline-size"
    };
    $[137] = t40;
  } else {
    t40 = $[137];
  }
  let t41;
  if ($[138] !== attachmentId || $[139] !== attachmentType) {
    t41 = <ChatTypingIndicator attachmentId={attachmentId} attachmentType={attachmentType} />;
    $[138] = attachmentId;
    $[139] = attachmentType;
    $[140] = t41;
  } else {
    t41 = $[140];
  }
  const t42 = `thread-${props.newCommentValues.threadId}-${editorSeed}-${submissionCount}`;
  const t43 = submissionCount > 0;
  const t44 = generateBusy || Boolean(crmLockedBy);
  const t45 = props.enableFileAttachments !== false;
  const t46 = props.placeholder || "Type your reply...";
  let t47;
  if ($[141] === Symbol.for("react.memo_cache_sentinel")) {
    t47 = <ChatConnectionStatus density="auto" />;
    $[141] = t47;
  } else {
    t47 = $[141];
  }
  let t48;
  if ($[142] !== canOfferGenerate || $[143] !== currentUser || $[144] !== hasText || $[145] !== isLoading || $[146] !== isUploading || $[147] !== setBoxBody || $[148] !== threadId) {
    t48 = canOfferGenerate && <ChatGenerateControl busy={isUploading || isLoading} hasTypedText={hasText} threadId={threadId} userId={currentUser.userId} onPending={setGenerateBusy} onWrite={(markdown, writtenForThreadId) => {
      if (writtenForThreadId !== threadIdRef.current) {
        return;
      }
      setBoxBody(markdown);
      setInitialMarkdown(markdown);
      setEditorSeed(`generated-${Date.now()}`);
    }} />;
    $[142] = canOfferGenerate;
    $[143] = currentUser;
    $[144] = hasText;
    $[145] = isLoading;
    $[146] = isUploading;
    $[147] = setBoxBody;
    $[148] = threadId;
    $[149] = t48;
  } else {
    t48 = $[149];
  }
  let t49;
  if ($[150] !== canUseDrafts || $[151] !== clearComposer || $[152] !== crm.lockedBy || $[153] !== crmDraft || $[154] !== crmLockedBy || $[155] !== crmOpen || $[156] !== deleteCrmDraft || $[157] !== draftCommentId || $[158] !== drafts || $[159] !== generateBusy || $[160] !== hasText || $[161] !== isLoading || $[162] !== isUploading || $[163] !== loadDraft || $[164] !== openCrmDraft || $[165] !== props.enableFileAttachments || $[166] !== removeDraft || $[167] !== saveDraft || $[168] !== sendCrmDraft || $[169] !== sendDraft || $[170] !== storedFileIds) {
    t49 = canUseDrafts && <DraftSaveButton busy={isUploading || isLoading || generateBusy || Boolean(crmLockedBy)} canSave={hasText || props.enableFileAttachments !== false && storedFileIds.length > 0} crmDraft={crmDraft} crmLocked={Boolean(crm.lockedBy)} crmOpen={crmOpen} drafts={drafts} openDraftId={draftCommentId} onDelete={removeDraft} onDeleteCrm={() => void deleteCrmDraft()} onNew={() => clearComposer(`new-${Date.now()}`)} onOpen={loadDraft} onOpenCrm={openCrmDraft} onSave={() => saveDraft()} onSend={sendDraft} onSendCrm={() => void sendCrmDraft()} />;
    $[150] = canUseDrafts;
    $[151] = clearComposer;
    $[152] = crm.lockedBy;
    $[153] = crmDraft;
    $[154] = crmLockedBy;
    $[155] = crmOpen;
    $[156] = deleteCrmDraft;
    $[157] = draftCommentId;
    $[158] = drafts;
    $[159] = generateBusy;
    $[160] = hasText;
    $[161] = isLoading;
    $[162] = isUploading;
    $[163] = loadDraft;
    $[164] = openCrmDraft;
    $[165] = props.enableFileAttachments;
    $[166] = removeDraft;
    $[167] = saveDraft;
    $[168] = sendCrmDraft;
    $[169] = sendDraft;
    $[170] = storedFileIds;
    $[171] = t49;
  } else {
    t49 = $[171];
  }
  const t50 = isUploading || generateBusy || Boolean(crmLockedBy);
  const t51 = isLoading || isUploading || generateBusy || upsertChatCommentMutation.loading;
  let t52;
  if ($[172] !== handleSubmit) {
    t52 = () => handleSubmit();
    $[172] = handleSubmit;
    $[173] = t52;
  } else {
    t52 = $[173];
  }
  let t53;
  if ($[174] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <LuArrowUp size={18} />;
    $[174] = t53;
  } else {
    t53 = $[174];
  }
  let t54;
  if ($[175] !== t50 || $[176] !== t51 || $[177] !== t52) {
    t54 = <IconButton aria-label="Post" borderRadius="md" disabled={t50} loading={t51} size="sm" variant="cta" onClick={t52}>{t53}</IconButton>;
    $[175] = t50;
    $[176] = t51;
    $[177] = t52;
    $[178] = t54;
  } else {
    t54 = $[178];
  }
  let t55;
  if ($[179] !== t48 || $[180] !== t49 || $[181] !== t54) {
    t55 = <HStack align="center" flexShrink={0} gap={2}>{t47}{t48}{t49}{t54}</HStack>;
    $[179] = t48;
    $[180] = t49;
    $[181] = t54;
    $[182] = t55;
  } else {
    t55 = $[182];
  }
  const t56 = isFull || isUploading || isLoading || generateBusy || Boolean(crmLockedBy);
  let t57;
  if ($[183] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = {
      hotKeys: {
        hotKeys: {
          enter: {
            func: event => {
              const native = (event as any)?.nativeEvent ?? event;
              if (event?.repeat || native?.isComposing) {
                return;
              }
              setSubmitTrigger(_temp5);
            }
          }
        }
      }
    };
    $[183] = t57;
  } else {
    t57 = $[183];
  }
  let t58;
  if ($[184] !== crm || $[185] !== crmOpen || $[186] !== setBoxBody) {
    t58 = values => {
      const next = values.markdown ?? "";
      if (crmOpen && next !== bodyRef.current) {
        crm.claim();
      }
      setBoxBody(next, true);
    };
    $[184] = crm;
    $[185] = crmOpen;
    $[186] = setBoxBody;
    $[187] = t58;
  } else {
    t58 = $[187];
  }
  let t59;
  if ($[188] !== addFile || $[189] !== crm || $[190] !== crmOpen) {
    t59 = file => {
      if (crmOpen) {
        crm.claim();
      }
      return addFile(file);
    };
    $[188] = addFile;
    $[189] = crm;
    $[190] = crmOpen;
    $[191] = t59;
  } else {
    t59 = $[191];
  }
  let t60;
  if ($[192] !== initialMarkdown || $[193] !== t42 || $[194] !== t43 || $[195] !== t44 || $[196] !== t45 || $[197] !== t46 || $[198] !== t55 || $[199] !== t56 || $[200] !== t58 || $[201] !== t59) {
    t60 = <RichMarkdownInput key={t42} autoFocus={t43} disabled={t44} enableFileAttachments={t45} initialMarkdownValue={initialMarkdown} placeholder={t46} typographyStyles={CHAT_EDITOR_TYPOGRAPHY} additionalToolbarButtons={t55} fileAttachmentsDisabled={t56} pluginConfigs={t57} onChange={t58} onFileAttachmentSelected={t59} />;
    $[192] = initialMarkdown;
    $[193] = t42;
    $[194] = t43;
    $[195] = t44;
    $[196] = t45;
    $[197] = t46;
    $[198] = t55;
    $[199] = t56;
    $[200] = t58;
    $[201] = t59;
    $[202] = t60;
  } else {
    t60 = $[202];
  }
  const t61 = isLoading || Boolean(crmLockedBy);
  let t62;
  if ($[203] !== pendingFiles || $[204] !== removeFile || $[205] !== t61) {
    t62 = <CommentPendingAttachments disabled={t61} files={pendingFiles} onRemove={removeFile} />;
    $[203] = pendingFiles;
    $[204] = removeFile;
    $[205] = t61;
    $[206] = t62;
  } else {
    t62 = $[206];
  }
  let t63;
  if ($[207] !== crmConflict || $[208] !== crmError || $[209] !== crmLockedBy || $[210] !== crmNote || $[211] !== crmOn || $[212] !== crmOpen || $[213] !== placeCrmDraft) {
    t63 = crmOn ? <ChatCrmDraftLine conflict={crmConflict} error={crmError} lockedBy={crmLockedBy} note={crmNote} open={crmOpen} onLoad={() => placeCrmDraft()} onKeepMine={() => {
      crmSavedKey.current = serverDraftKey(crmRef.current.context);
      setCrmConflict(false);
    }} /> : null;
    $[207] = crmConflict;
    $[208] = crmError;
    $[209] = crmLockedBy;
    $[210] = crmNote;
    $[211] = crmOn;
    $[212] = crmOpen;
    $[213] = placeCrmDraft;
    $[214] = t63;
  } else {
    t63 = $[214];
  }
  let t64;
  if ($[215] !== t60 || $[216] !== t62 || $[217] !== t63) {
    t64 = <OnboardingHighlightTarget borderRadius="lg" id={ONBOARDING_HIGHLIGHT_IDS.aiChat}><Box width="full">{t60}{t62}{t63}</Box></OnboardingHighlightTarget>;
    $[215] = t60;
    $[216] = t62;
    $[217] = t63;
    $[218] = t64;
  } else {
    t64 = $[218];
  }
  let t65;
  if ($[219] !== props.boxProps || $[220] !== t41 || $[221] !== t64) {
    t65 = <Box css={t40} paddingBottom={2} paddingTop={1} paddingX={2} width="full" {...props.boxProps}>{t41}{t64}</Box>;
    $[219] = props.boxProps;
    $[220] = t41;
    $[221] = t64;
    $[222] = t65;
  } else {
    t65 = $[222];
  }
  return t65;
};
ChatMessageComposer.mutations = {
  upsertChatComment: () => gql`
    mutation ($input: UpsertChatCommentInput!) {
      upsertChatComment(input: $input) {
        comment {
          id
          commentId
          isDraft
          ${COMMENT_STORED_FILES_SELECTION}
        }
      }
    }
  `,
  deleteComment: () => gql`
    mutation ($input: DeleteCommentInput!) {
      deleteComment(input: $input) {
        comment {
          id
          commentId
          deletedAt
        }
      }
    }
  `
};
ChatMessageComposer.queries = {
  myDrafts: () => gql`
    query (
      $collectionId: String
      $order: [[String]]
      $pageSize: Int
      $parentCommentId: String
      $threadId: String
    ) {
      forum {
        id
        comments(
          collectionId: $collectionId
          onlyMyDrafts: true
          order: $order
          pageSize: $pageSize
          parentCommentId: $parentCommentId
          threadId: $threadId
        ) {
          id
          items {
            id
            commentId
            body
            isDraft
            createdAt
            updatedAt
            ${COMMENT_STORED_FILES_SELECTION}
          }
        }
      }
    }
  `
};
export const stripMarkdownWhitespace = (str: string) => {
  return (str || '').replaceAll('\n', '').replaceAll('\r', '').replaceAll('<br>', '').trim();
};

/**
 * Under the box in CRM mode: who else is working the reply (the open CRM draft is locked), a change made
 * in the CRM while this box had its own edits (Load / Keep mine), an error, or what the box is now.
 */
function ChatCrmDraftLine(props) {
  const $ = _c(14);
  if (props.lockedBy) {
    let t0;
    if ($[0] !== props.lockedBy) {
      t0 = <Text color="fg.muted" fontSize="xs" paddingTop={1} role="status">{props.lockedBy} is working on this reply in the CRM. You can edit it again when they finish.</Text>;
      $[0] = props.lockedBy;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  if (props.conflict) {
    let t0;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <Text color="fg" fontSize="xs">This reply was changed in the CRM.</Text>;
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    let t1;
    if ($[3] !== props.onLoad) {
      t1 = <Button size="2xs" variant="outline" onClick={props.onLoad}>Load it</Button>;
      $[3] = props.onLoad;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    let t2;
    if ($[5] !== props.onKeepMine) {
      t2 = <Button size="2xs" variant="ghost" onClick={props.onKeepMine}>Keep mine</Button>;
      $[5] = props.onKeepMine;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    let t3;
    if ($[7] !== t1 || $[8] !== t2) {
      t3 = <HStack gap={2} paddingTop={1} role="alert" wrap="wrap">{t0}{t1}{t2}</HStack>;
      $[7] = t1;
      $[8] = t2;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    return t3;
  }
  if (props.error) {
    let t0;
    if ($[10] !== props.error) {
      t0 = <Text color="fg.error" fontSize="xs" paddingTop={1} role="alert">{props.error}</Text>;
      $[10] = props.error;
      $[11] = t0;
    } else {
      t0 = $[11];
    }
    return t0;
  }
  const line = props.note || (props.open ? "Editing your CRM reply draft. Save updates it in your CRM inbox too." : null);
  let t0;
  if ($[12] !== line) {
    t0 = line ? <Text color="fg.subtle" fontSize="xs" paddingTop={1} role="status">{line}</Text> : null;
    $[12] = line;
    $[13] = t0;
  } else {
    t0 = $[13];
  }
  return t0;
}
function DraftSaveButton(props) {
  const $ = _c(43);
  const count = props.drafts.length + (props.crmDraft ? 1 : 0);
  const saveDisabled = props.busy || count === 0 && !props.canSave;
  const t0 = count > 0 ? `Save draft, ${count} saved` : "Save draft";
  let t1;
  if ($[0] !== saveDisabled) {
    t1 = saveDisabled ? {
      pointerEvents: "none"
    } : undefined;
    $[0] = saveDisabled;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== props) {
    t2 = () => {
      if (props.canSave) {
        props.onSave();
      }
    };
    $[2] = props;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <LuSave size={18} />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== saveDisabled || $[6] !== t0 || $[7] !== t1 || $[8] !== t2) {
    t4 = <IconButton aria-label={t0} borderRadius="md" css={t1} disabled={saveDisabled} size="sm" variant="ghost" onClick={t2}>{t3}</IconButton>;
    $[5] = saveDisabled;
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== count) {
    t5 = count > 0 && <Box alignItems="center" bg="fg" borderRadius="full" boxShadow="0 0 0 2px var(--chakra-colors-bg)" color="bg" display="flex" fontSize="10px" fontWeight="semibold" height="16px" justifyContent="center" lineHeight="1" minWidth="16px" paddingX="3px" pointerEvents="none" position="absolute" right="-3px" top="-3px">{count}</Box>;
    $[10] = count;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t4 || $[13] !== t5) {
    t6 = <Box display="inline-flex" position="relative">{t4}{t5}</Box>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const button = t6;
  if (count === 0) {
    let t7;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = {
        placement: "top"
      };
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    let t8;
    if ($[16] !== button) {
      t8 = <Tooltip borderRadius="6px" content="Save draft" fontSize="11px" fontWeight="bold" letterSpacing="0.05em" positioning={t7} textTransform="uppercase">{button}</Tooltip>;
      $[16] = button;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    return t8;
  }
  let t7;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      placement: "top-end"
    };
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== button) {
    t8 = <HoverCardTrigger asChild={true}>{button}</HoverCardTrigger>;
    $[19] = button;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Badge color="colorPalette.fg/50" paddingX={2} paddingY={1} size="xs" variant="title">Drafts</Badge>;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== props.crmOpen || $[23] !== props.onNew || $[24] !== props.openDraftId) {
    t10 = (props.openDraftId || props.crmOpen) && <HStack _hover={{
      bg: "colorPalette.subtle"
    }} borderRadius="md" cursor="pointer" gap={2} paddingX={2} paddingY={1} onClick={props.onNew}><LuPlus size={14} /><Text fontSize="sm">New draft</Text></HStack>;
    $[22] = props.crmOpen;
    $[23] = props.onNew;
    $[24] = props.openDraftId;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== props.busy || $[27] !== props.crmDraft || $[28] !== props.crmLocked || $[29] !== props.crmOpen || $[30] !== props.onDeleteCrm || $[31] !== props.onOpenCrm || $[32] !== props.onSendCrm) {
    t11 = props.crmDraft ? <CrmDraftRow busy={props.busy} draft={props.crmDraft} locked={Boolean(props.crmLocked)} open={Boolean(props.crmOpen)} onDelete={props.onDeleteCrm} onOpen={props.onOpenCrm} onSend={props.onSendCrm} /> : null;
    $[26] = props.busy;
    $[27] = props.crmDraft;
    $[28] = props.crmLocked;
    $[29] = props.crmOpen;
    $[30] = props.onDeleteCrm;
    $[31] = props.onOpenCrm;
    $[32] = props.onSendCrm;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== props) {
    t12 = props.drafts.map(draft => {
      if (!draft.commentId) {
        return null;
      }
      const preview = stripMarkdownWhitespace(draft.body || "") || "Empty draft";
      const open = draft.commentId === props.openDraftId;
      const canSend = stripMarkdownWhitespace(draft.body || "").length > 0;
      return <HStack key={draft.commentId} borderRadius="md" gap={0} paddingY={0.5}><Box _hover={{
          bg: "colorPalette.subtle"
        }} aria-current={open ? "true" : undefined} aria-label={`Edit draft ${preview}`} as="button" bg={open ? "colorPalette.subtle" : "transparent"} borderRadius="md" flex={1} minWidth={0} paddingX={2} paddingY={1} textAlign="left" type="button" onClick={() => props.onOpen(draft)}><Text fontSize="sm" fontWeight={open ? "medium" : "normal"} lineClamp={1}>{preview}</Text><Text color="colorPalette.fg/50" fontSize="xs">{draftAge(draft.updatedAt || draft.createdAt)}</Text></Box><IconButton aria-label={`Send draft ${preview}`} disabled={!canSend || props.busy} size="xs" variant="ghost" onClick={() => props.onSend(draft)}><LuSend size={14} /></IconButton><IconButton aria-label={`Delete draft ${preview}`} disabled={props.busy} size="xs" variant="ghost" onClick={() => props.onDelete(draft.commentId as string)}><LuTrash2 size={14} /></IconButton></HStack>;
    });
    $[34] = props;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== t10 || $[37] !== t11 || $[38] !== t12) {
    t13 = <HoverCardContent padding={1} width="16rem">{t9}<Box maxHeight="14rem" overflowY="auto">{t10}{t11}{t12}</Box></HoverCardContent>;
    $[36] = t10;
    $[37] = t11;
    $[38] = t12;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== t13 || $[41] !== t8) {
    t14 = <HoverCardRoot closeDelay={200} openDelay={150} positioning={t7}>{t8}{t13}</HoverCardRoot>;
    $[40] = t13;
    $[41] = t8;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  return t14;
}

/** The CRM reply draft's row in the drafts menu: open it, send it through the CRM, or delete it. */
function CrmDraftRow(props) {
  const $ = _c(31);
  let t0;
  if ($[0] !== props.draft.body) {
    t0 = stripMarkdownWhitespace(props.draft.body || "") || "Empty draft";
    $[0] = props.draft.body;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const preview = t0;
  const t1 = props.draft.body || "";
  let t2;
  if ($[2] !== t1) {
    t2 = stripMarkdownWhitespace(t1);
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const canSend = t2.length > 0;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      bg: "colorPalette.subtle"
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = props.open ? "true" : undefined;
  const t5 = `Edit CRM reply draft ${preview}`;
  const t6 = props.open ? "colorPalette.subtle" : "transparent";
  const t7 = props.open ? "medium" : "normal";
  let t8;
  if ($[5] !== preview || $[6] !== t7) {
    t8 = <Text fontSize="sm" fontWeight={t7} lineClamp={1}>{preview}</Text>;
    $[5] = preview;
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const t9 = props.locked ? "CRM reply \xB7 someone is working on it" : "CRM reply \xB7 same draft in your CRM inbox";
  let t10;
  if ($[8] !== t9) {
    t10 = <Text color="colorPalette.fg/50" fontSize="xs">{t9}</Text>;
    $[8] = t9;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== props.onOpen || $[11] !== t10 || $[12] !== t4 || $[13] !== t5 || $[14] !== t6 || $[15] !== t8) {
    t11 = <chakra.button _hover={t3} aria-current={t4} aria-label={t5} bg={t6} borderRadius="md" flex={1} minWidth={0} paddingX={2} paddingY={1} textAlign="left" type="button" onClick={props.onOpen}>{t8}{t10}</chakra.button>;
    $[10] = props.onOpen;
    $[11] = t10;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t8;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const t12 = `Send CRM reply draft ${preview}`;
  const t13 = !canSend || props.busy || props.locked;
  let t14;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <LuSend size={14} />;
    $[17] = t14;
  } else {
    t14 = $[17];
  }
  let t15;
  if ($[18] !== props.onSend || $[19] !== t12 || $[20] !== t13) {
    t15 = <IconButton aria-label={t12} disabled={t13} size="xs" variant="ghost" onClick={props.onSend}>{t14}</IconButton>;
    $[18] = props.onSend;
    $[19] = t12;
    $[20] = t13;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  const t16 = `Delete CRM reply draft ${preview}`;
  const t17 = props.busy || props.locked;
  let t18;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <LuTrash2 size={14} />;
    $[22] = t18;
  } else {
    t18 = $[22];
  }
  let t19;
  if ($[23] !== props.onDelete || $[24] !== t16 || $[25] !== t17) {
    t19 = <IconButton aria-label={t16} disabled={t17} size="xs" variant="ghost" onClick={props.onDelete}>{t18}</IconButton>;
    $[23] = props.onDelete;
    $[24] = t16;
    $[25] = t17;
    $[26] = t19;
  } else {
    t19 = $[26];
  }
  let t20;
  if ($[27] !== t11 || $[28] !== t15 || $[29] !== t19) {
    t20 = <HStack borderRadius="md" gap={0} paddingY={0.5}>{t11}{t15}{t19}</HStack>;
    $[27] = t11;
    $[28] = t15;
    $[29] = t19;
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  return t20;
}
function draftAge(value?: string | null) {
  if (!value) {
    return '';
  }
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) {
    return '';
  }
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (minutes < 1) {
    return 'Just now';
  }
  if (minutes < 60) {
    return `${minutes}m ago`;
  }
  const hours = Math.round(minutes / 60);
  if (hours < 24) {
    return `${hours}h ago`;
  }
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}
function _temp(count) {
  return count + 1;
}
function _temp2(count_0) {
  return count_0 + 1;
}
function _temp3(count_1) {
  return count_1 + 1;
}
function _temp4(count_2) {
  return count_2 + 1;
}
function _temp5(x) {
  return x + 1;
}